import { FunctionComponent, useState } from 'react';
import {
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Stack,
    Tab,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
    IntegrationConfiguration,
    ProductConfiguration,
} from '@/models/product';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import { useAuthentication } from '@/hooks/useAuthentication';

type BookingOptionTabsProps = {
    productConfiguration: ProductConfiguration;
    onProductConfigurationChange: (
        productConfiguration: ProductConfiguration
    ) => void;
};

export const BookingOptionTabs: FunctionComponent<BookingOptionTabsProps> = ({
    productConfiguration,
    onProductConfigurationChange,
}) => {
    const [tabValue, setTabValue] = useState('portal');

    const handleTabChange = (_event, newValue: string) => {
        setTabValue(newValue);
    };

    const onConfigurationChange =
        (key: keyof ProductConfiguration) =>
        (config: IntegrationConfiguration) => {
            onProductConfigurationChange({
                ...productConfiguration,
                [key]: config,
            });
        };

    return (
        <TabContext value={tabValue}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleTabChange}>
                    <Tab label='starjack.com & starjack App' value='portal' />

                    <Tab
                        label='Webshop auf meiner Webseite'
                        value='integration'
                    />
                    <Tab label='starjack.B2B' value='b2b' />

                    <Tab label='Hospitaltiy' value='hospitality' />
                </TabList>
            </Box>

            <TabPanel value='portal'>
                <CheckboxBlock
                    checkBoxConfig={productConfiguration.portalConfiguration}
                    onCheckboxChecked={onConfigurationChange(
                        'portalConfiguration'
                    )}
                />
            </TabPanel>

            <TabPanel value='integration'>
                <CheckboxBlock
                    checkBoxConfig={
                        productConfiguration.integrationConfiguration
                    }
                    onCheckboxChecked={onConfigurationChange(
                        'integrationConfiguration'
                    )}
                />
            </TabPanel>
            <TabPanel value='b2b'>
                <CheckboxBlock
                    checkBoxConfig={productConfiguration.b2bConfiguration}
                    onCheckboxChecked={onConfigurationChange(
                        'b2bConfiguration'
                    )}
                />
            </TabPanel>

            <TabPanel value='hospitality'>
                <CheckboxBlock
                    checkBoxConfig={
                        productConfiguration.hospitalityConfiguration
                    }
                    onCheckboxChecked={onConfigurationChange(
                        'hospitalityConfiguration'
                    )}
                />
            </TabPanel>
        </TabContext>
    );
};

type CheckboxBlockProps = {
    onCheckboxChecked: (checked: IntegrationConfiguration) => void;
    checkBoxConfig: IntegrationConfiguration;
};

export const CheckboxBlock: FunctionComponent<CheckboxBlockProps> = ({
    checkBoxConfig,
    onCheckboxChecked,
}) => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const { data: liftOperator } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorId,
    });

    const onChange = (key: keyof IntegrationConfiguration) => (e) => {
        const { checked } = e.target;

        onCheckboxChecked({ ...checkBoxConfig, [key]: checked });
    };

    return (
        <Stack spacing={2}>
            <FormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={onChange('keycard')}
                            checked={checkBoxConfig.keycard}
                        />
                    }
                    label='KeyCard
'
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={onChange('barcode')}
                            checked={checkBoxConfig.barcode}
                        />
                    }
                    label='e-Ticket (Zutritt mit Barcode / QR-Code-Scanner)'
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={onChange('pickup')}
                            checked={checkBoxConfig.pickup}
                        />
                    }
                    label='pick-up-Voucher (Abholung Automat oder Kassa)'
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={onChange('internalProduction')}
                            checked={checkBoxConfig.internalProduction}
                        />
                    }
                    label='Produktion und Versand durch die Bergbahn'
                />

                {liftOperator?.mobileFlowEnabled && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={onChange('mobileFlow')}
                                checked={checkBoxConfig.mobileFlow}
                            />
                        }
                        label='Smartphone Ticket'
                    />
                )}
            </FormGroup>
        </Stack>
    );
};
